/**
 * FICHAS — o histórico de operações, a agenda do comprador. Ano → mês → dia.
 * Mostra o que está em andamento, o que falta pagar e o que vence na semana.
 * Para exportar: «Selecionar», marcar as fichas (de qualquer data) e Excel.
 *
 * R2B (revisão de 06/10/2026): o «a pagar» e o «a receber» saem de
 * `contasDaFicha` (src/dominio/financeiro.ts), a mesma conta do Pagamento, do
 * Custo, da tela Contas, do Relatório e do Excel. A ficha de Venda é «a
 * receber» (antes aparecia «a pagar»); a de Conferência não entra em nenhum.
 */
import { useMemo, useState, type ReactElement } from 'react'
import { baixar } from '../dados/arquivos'
import { usarEstado } from '../dados/armazem'
import { quitacoesDa, usarPagamentos } from '../dados/pagamentos'
import { usarFerramenta, usarPode, usarSessao } from '../dados/sessao'
import { calcular, custoPendente, situacaoDePagamento } from '../dominio/contas'
import { contasDaFicha, resumirContas } from '../dominio/financeiro'
import { abasDasFichas, destinoInterno, gtasDa } from '../dominio/exportar'
import { divisaoDoPagamento } from '../dominio/destinos'
import { dataBR, diaDaSemana, hoje, kg, MESES, normalizar, reais } from '../dominio/numeros'
import { montarXlsx } from '../dominio/planilha'
import type { Ficha } from '../dominio/tipos'
import { avisar, type Ir } from '../ui'
import { BarraDeSincronia } from './Sincronia'
import { recalcular, usarCatira } from '../dados/catira'

type Filtro = 'todas' | 'aberta' | 'apagar' | 'semana' | 'Compra' | 'Venda' | 'cancelada' | 'areceber'

function maisDias(d: string, n: number): string {
  const [a, m, dd] = d.split('-').map(Number)
  const x = new Date(a!, m! - 1, dd! + n)
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`
}

export function Fichas({ ir }: { ir: Ir }) {
  const { fichas: todas, recusadas } = usarEstado()
  const pg = usarPagamentos()
  const sessao = usarSessao()
  // «comissão pendente» é aviso só do gestor: o comprador nunca sabe da comissão
  const gestor = sessao.tipo === 'dentro' && sessao.eu.pessoa.papel === 'gestor'
  const comprador = sessao.tipo === 'dentro' && sessao.eu.pessoa.papel === 'comprador'
  // 3E: a Consulta vê as fichas e não abre nenhuma
  const podeCriar = usarPode()('criarFicha')
  // «excluída» some das listas (o banco guarda)
  const fichas = useMemo(() => todas.filter((f) => f.situacao !== 'excluida'), [todas])
  const [busca, setBusca] = useState('')
  const [filtroEscolhido, setFiltro] = useState<Filtro>('todas')
  const [selecao, setSelecao] = useState<Set<string> | null>(null)
  // 3C: o que o gestor ligou em Ajustes › Ferramentas (desligada só esconde)
  const comCatira = usarFerramenta('catira')
  const comNotas = usarFerramenta('notas')
  const comPagamentos = usarFerramenta('comissoes')
  const comCusto = usarFerramenta('fechamento')
  // P3: o relatório tem as comissões — só gestor e Consulta, e só com a ferramenta ligada
  const veRelatorio = usarFerramenta('relatorio') && sessao.tipo === 'dentro' && sessao.eu.pessoa.papel !== 'comprador'
  // filtro de pagamento escolhido antes de a ferramenta ser desligada: volta a «Todas»
  const filtro: Filtro = !comPagamentos && (filtroEscolhido === 'apagar' || filtroEscolhido === 'semana' || filtroEscolhido === 'areceber') ? 'todas' : filtroEscolhido
  // R2B: comissões e revenda entram no «a pagar/a receber» só de quem as vê (gestor e Consulta)
  const veFinanceiro = sessao.tipo === 'dentro' && sessao.eu.pessoa.papel !== 'comprador'
  // R2B: a tela Contas (a pagar e a receber por vencimento) — gestor e Consulta, com «Comissões e pagamentos»
  const veContas = comPagamentos && veFinanceiro
  // D3: a catira é do gestor (a Consulta vê); o comprador nem vê a entrada
  const veCatira = comCatira && sessao.tipo === 'dentro' && sessao.eu.pessoa.papel !== 'comprador'
  const catira = usarCatira()
  const cabNaCatira = useMemo(() => recalcular(catira.movimentos).cabecas, [catira.movimentos])

  const limiteSemana = maisDias(hoje(), 7)
  const info = useMemo(() => new Map(fichas.map((f) => {
    const r = calcular(f)
    // 3F: o «a pagar» é só a parte do gestor — a das porteiras Cliente (intermediação) o cliente paga direto
    const dv = divisaoDoPagamento(f)
    const p = situacaoDePagamento(f, f.finalidade === 'Venda' ? r.liquido : dv.doGestor, quitacoesDa(pg, f.id))
    // R2B: as contas da ficha — a mesma conta de todas as telas. Venda é «a receber».
    const contas = contasDaFicha(f, pg, { verComissao: veFinanceiro })
    const res = resumirContas(contas)
    const vencimentos = contas.filter((c) => c.sentido === 'pagar' && c.falta > 0 && c.vencimento).map((c) => c.vencimento!).sort()
    // 3C: sem «Comissões e pagamentos» ninguém lança o pago — «a pagar» e
    // «vence» só cresceriam para sempre, então somem junto com a ferramenta
    const devendo = comPagamentos && res.aPagar > 0
    const aReceber = comPagamentos && res.aReceber > 0
    // R2B: ficha pesada pelo próprio gestor não tem comissão de comprador — nada pendente
    const semComissao = comPagamentos && gestor && f.situacao === 'encerrada' && !pg.comissoes[f.id] && f.pesadoPorGestor !== true && f.finalidade !== 'Conferência'
    // R1C: «custo pendente» não aparece para o comprador (revisão de 06/10/2026)
    const semCusto = comCusto && !comprador && custoPendente(f, r)
    const aCargoDoCliente = f.situacao === 'encerrada' && r.temPreco ? dv.aCargoDoCliente : 0
    return [f.id, {
      r, p, devendo, semComissao, semCusto, aCargoDoCliente, mostraPago: comPagamentos,
      venceSemana: devendo && !!vencimentos[0] && vencimentos[0] <= limiteSemana,
      // R2B: o que a lista e o painel somam (o mesmo número do Pagamento e da tela Contas)
      aPagar: res.aPagar, aReceber: aReceber ? res.aReceber : 0, aMais: comPagamentos ? res.aMais : 0, proximo: vencimentos[0] ?? null,
    }]
  })), [fichas, limiteSemana, pg, gestor, comprador, comPagamentos, comCusto, veFinanceiro])

  const andamento = fichas.filter((f) => f.situacao === 'aberta').length
  const devendo = fichas.filter((f) => info.get(f.id)?.devendo)
  const totalAPagar = devendo.reduce((s, f) => s + Math.round((info.get(f.id)?.aPagar ?? 0) * 100), 0) / 100
  // R2B: o quarto quadro — venda, revenda a receber e Comissão do cliente
  const recebendo = fichas.filter((f) => (info.get(f.id)?.aReceber ?? 0) > 0)
  const totalAReceber = recebendo.reduce((s, f) => s + Math.round((info.get(f.id)?.aReceber ?? 0) * 100), 0) / 100
  const semana = fichas.filter((f) => info.get(f.id)?.venceSemana).length

  const q = normalizar(busca)
  const lista = fichas.filter((f) => {
    const i = info.get(f.id)!
    if (filtro === 'aberta' || filtro === 'cancelada') { if (f.situacao !== filtro) return false }
    else if (filtro === 'apagar') { if (!i.devendo) return false }
    else if (filtro === 'areceber') { if (!(i.aReceber > 0)) return false }
    else if (filtro === 'semana') { if (!i.venceSemana) return false }
    else if (filtro !== 'todas' && f.finalidade !== filtro) return false
    if (!q) return true
    return normalizar([f.numero, f.produtor.nome, f.produtor.propriedade, f.produtor.municipio, f.destino?.nome, f.destino?.municipio, destinoInterno(f), ...gtasDa(f),
      ...f.registros.map((r) => r.obs)].join(' ')).includes(q)
  }).sort((a, b) => (b.data + b.numero).localeCompare(a.data + a.numero))

  function alternar(id: string) {
    if (!selecao) return
    const s = new Set(selecao)
    if (s.has(id)) s.delete(id); else s.add(id)
    setSelecao(s)
  }
  function exportar() {
    const escolhidas = fichas.filter((f) => selecao?.has(f.id)).sort((a, b) => (a.data + a.numero).localeCompare(b.data + b.numero))
    if (!escolhidas.length) { window.alert('Marque ao menos uma ficha.'); return }
    const datas = escolhidas.map((f) => f.data)
    const nome = `fichas-${datas[0]}${datas.length > 1 && datas[datas.length - 1] !== datas[0] ? '_a_' + datas[datas.length - 1] : ''}.xlsx`
    const verComissao = sessao.tipo === 'dentro' && sessao.eu.pessoa.papel !== 'comprador'
    // R2B: a mesma regra do painel (só encerrada; compra «a pagar», venda «a receber»)
    baixar(montarXlsx(abasDasFichas(escolhidas, pg, { ver: verComissao })), nome, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
    avisar(`${escolhidas.length} ficha(s) exportada(s)`)
    setSelecao(null)
  }

  // agrupamento ano → mês → dia
  const blocos: ReactElement[] = []
  let ano = '', mes = '', dia = ''
  let doMes: ReactElement[] = []
  const fecharMes = () => {
    if (!mes) return
    const m = mes
    const ids = lista.filter((f) => f.data.startsWith(m)).map((f) => f.id)
    blocos.push(
      <div className="cartao" key={'m' + m}>
        <div className="mes">
          <b>{MESES[Number(m.slice(5)) - 1]}</b>
          {selecao && <button className="mini" onClick={() => setSelecao(new Set([...selecao, ...ids]))}>Marcar o mês</button>}
        </div>
        {doMes}
      </div>)
    doMes = []
  }
  for (const f of lista) {
    const a = f.data.slice(0, 4), m = f.data.slice(0, 7)
    if (m !== mes) { fecharMes(); mes = m; dia = '' }
    if (a !== ano) { ano = a; blocos.push(<h2 className="secao ano" key={'a' + a}>{a}</h2>) }
    if (f.data !== dia) { dia = f.data; doMes.push(<div className="dia" key={'d' + f.data}>{dataBR(f.data).slice(0, 5)} · {diaDaSemana(f.data)}</div>) }
    doMes.push(<Linha key={f.id} f={f} i={info.get(f.id)!} recusada={recusadas.fichas[f.id]} marcada={selecao?.has(f.id)} selecionando={!!selecao}
      tocar={() => (selecao ? alternar(f.id) : ir({ tela: 'ficha', id: f.id, aba: f.situacao === 'aberta' ? 'pesagem' : 'romaneio' }))} />)
  }
  fecharMes()

  return (
    <div>
      <BarraDeSincronia />
      {/* D4: notas fiscais ao lado da Catira — todos os papéis (o comprador registra as das fichas e despesas dele) */}
      {(veCatira || comNotas || veRelatorio || veContas) && (
        <div className="linha">
          {veContas && <button className="botao fantasma" onClick={() => ir({ tela: 'contas' })} title="Contas a pagar e a receber">💰 Contas</button>}
          {veCatira && (
            <button className="botao fantasma" onClick={() => ir({ tela: 'catira' })}>🐂 Meu estoque — {cabNaCatira} cab</button>
          )}
          {comNotas && <button className="botao fantasma" onClick={() => ir({ tela: 'notas' })}>🧾 Notas fiscais</button>}
          {veRelatorio && <button className="botao fantasma" onClick={() => ir({ tela: 'relatorio' })}>📊 Receitas e despesas</button>}
        </div>
      )}
      {podeCriar && <button className="botao grande" onClick={() => ir({ tela: 'nova' })}>+ Nova pesagem</button>}
      <div className="painel">
        <button className={filtro === 'aberta' ? 'on' : ''} onClick={() => setFiltro(filtro === 'aberta' ? 'todas' : 'aberta')}><b>{andamento}</b><span>em andamento</span></button>
        {comPagamentos && <button className={filtro === 'apagar' ? 'on' : ''} onClick={() => setFiltro(filtro === 'apagar' ? 'todas' : 'apagar')}><b>{reais(totalAPagar)}</b><span>a pagar ({devendo.length})</span></button>}
        {comPagamentos && <button className={filtro === 'semana' ? 'on' : ''} onClick={() => setFiltro(filtro === 'semana' ? 'todas' : 'semana')}><b>{semana}</b><span>vencem em 7 dias</span></button>}
        {comPagamentos && <button className={filtro === 'areceber' ? 'on' : ''} onClick={() => setFiltro(filtro === 'areceber' ? 'todas' : 'areceber')}><b>{reais(totalAReceber)}</b><span>a receber ({recebendo.length})</span></button>}
      </div>
      {/* R2B: uma data só na lista, explicada */}
      <div className="mini-dica nao-imprimir">Cada ficha aparece no dia dela (a data da Origem). Dentro da ficha: «aberta em» e «pesada em».</div>
      <div className="cartao">
        <input type="search" value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar produtor, nº, município, porteira, GTA" />
        <div className="linha" style={{ marginTop: 8 }}>
          <select value={filtro} onChange={(e) => setFiltro(e.target.value as Filtro)}>
            <option value="todas">Todas</option><option value="aberta">Em andamento</option>
            {comPagamentos && <option value="apagar">A pagar</option>}
            {comPagamentos && <option value="semana">Vencem em 7 dias</option>}
            {comPagamentos && <option value="areceber">A receber</option>}<option value="Compra">Compras</option><option value="Venda">Vendas</option>
            <option value="cancelada">Canceladas</option>
          </select>
          {selecao
            ? <button className="mini alto" onClick={() => setSelecao(null)}>Cancelar</button>
            : <button className="mini alto" onClick={() => setSelecao(new Set())}>Selecionar</button>}
        </div>
        {selecao && (
          <button className="botao" onClick={exportar}>Exportar {selecao.size} para Excel</button>
        )}
      </div>
      {lista.length ? blocos : (
        <div className="cartao vazio">{fichas.length ? 'Nada encontrado.' : podeCriar ? 'Nenhuma ficha ainda. Toque em Nova pesagem para começar.' : 'Nenhuma ficha ainda.'}</div>
      )}
    </div>
  )
}

function Linha({ f, i, recusada, marcada, selecionando, tocar }: {
  f: Ficha; i: { r: ReturnType<typeof calcular>; p: ReturnType<typeof situacaoDePagamento>; devendo: boolean; semComissao: boolean; semCusto: boolean; aCargoDoCliente: number; mostraPago: boolean; venceSemana: boolean
    aPagar: number; aReceber: number; aMais: number; proximo: string | null }
  recusada?: string; marcada?: boolean; selecionando: boolean; tocar: () => void
}) {
  const { r, p } = i
  return (
    <div className={`item clicavel${marcada ? ' marcada' : ''}`} onClick={tocar}>
      <div className="item-topo">
        <span className="item-titulo">{selecionando && <input type="checkbox" checked={!!marcada} readOnly />} {f.produtor.nome}</span>
        <span className="item-valor">{r.temPreco && f.situacao !== 'cancelada' ? reais(r.liquido) : `${kg(r.kg)} kg`}</span>
      </div>
      {/* R1C: no celular o número da ficha não quebra, e as etiquetas ganham linha própria, que quebra */}
      <div className="item-baixo">
        <span><span className="sem-quebra">Nº {f.numero}</span> · {f.finalidade} · {r.cab} cab{r.kg ? ` · ${kg(r.kg)} kg` : ''}</span>
      </div>
      <div className="etiquetas">
        <span>
          {/* 3E: o banco recusou de vez — fica só neste aparelho, fora da fila */}
          {recusada && <span className="etiqueta cancelada" title={recusada}>recusada pelo servidor</span>}
          {f.situacao === 'aberta' && <span className="etiqueta aberta">em andamento</span>}
          {f.situacao === 'cancelada' && <span className="etiqueta cancelada">cancelada</span>}
          {i.devendo && <span className={`etiqueta ${i.venceSemana ? 'aberta' : 'neutra'}`}>{i.proximo ? `vence ${dataBR(i.proximo).slice(0, 5)} · ` : ''}a pagar {reais(i.aPagar)}{i.mostraPago && f.situacao === 'encerrada' && p.quitado ? ' (frete, gastos ou comissão)' : ''}</span>}
          {i.aReceber > 0 && <span className="etiqueta neutra">a receber {reais(i.aReceber)}</span>}
          {i.mostraPago && f.situacao === 'encerrada' && p.quitado && <span className="etiqueta ok">{f.finalidade === 'Venda' ? 'recebido' : 'vendedor pago'}</span>}
          {i.aMais > 0 && <span className="etiqueta cancelada" title="pago ou recebido acima do devido — confira na aba Pagamento">{f.finalidade === 'Venda' ? 'recebido' : 'pago'} a mais {reais(i.aMais)}</span>}
          {i.mostraPago && i.aCargoDoCliente > 0 && <span className="etiqueta neutra" title="intermediação: o cliente paga direto ao vendedor">{reais(i.aCargoDoCliente)} a cargo do cliente</span>}
          {i.semComissao && <span className="etiqueta aberta">comissão pendente</span>}
          {i.semCusto && <span className="etiqueta aberta">custo pendente</span>}
        </span>
      </div>
    </div>
  )
}
